import { useEffect } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function TermsConditionsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.alwaz.travel';
    const scripts: HTMLScriptElement[] = [];

    document.title = 'Terms & Conditions | Alwaz Travels – Booking & Travel Policy';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Read the Alwaz Travels Terms & Conditions. Understand our booking policy, cancellation charges, payment terms, liability limitations, and customer responsibilities for all travel services.');

    const metaRobots = document.querySelector('meta[name="robots"]');
    if (metaRobots) metaRobots.setAttribute('content', 'noindex, follow');

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Terms & Conditions | Alwaz Travels',
      description: 'Alwaz Travels Terms & Conditions – booking policy, cancellation charges, payment terms, and customer responsibilities.',
      url: `${siteUrl}/terms-and-conditions`,
      isPartOf: { '@type': 'WebSite', name: 'Alwaz Travels', url: siteUrl },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Terms & Conditions', item: `${siteUrl}/terms-and-conditions` },
        ],
      },
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    scripts.push(script);

    return () => {
      scripts.forEach((s) => s.parentNode?.removeChild(s));
      if (metaRobots) metaRobots.setAttribute('content', 'index, follow');
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <div className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Terms &amp; Conditions
          </h1>
          <p className="text-gray-600 mb-12">
            Last Updated: January 2025
          </p>

          <div className="space-y-12">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                1. Introduction &amp; Acceptance of Terms
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Welcome to <strong>Alwaz Travel London</strong>, operated by <strong>Nex Pro Travels Limited</strong>, a company registered in England and Wales. By accessing our website, using our services, or making a booking with us, you agree to be bound by these Terms &amp; Conditions.
                </p>
                <p>
                  Please read these terms carefully before using our services. If you do not agree with any part of these terms, you must not use our website or services.
                </p>
                <p>
                  These terms constitute a legally binding agreement between you (the customer) and Nex Pro Travels Limited trading as Alwaz Travel London.
                </p>
              </div>
            </section>

            {/* Company Details */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                2. Company Details
              </h2>
              <div className="bg-gray-50 p-6 rounded-lg space-y-2 text-gray-700">
                <p><strong>Company Name:</strong> Nex Pro Travels Limited</p>
                <p><strong>Trading As:</strong> Alwaz Travel London</p>
                <p><strong>Registered in:</strong> England and Wales</p>
                <p><strong>Email:</strong> <a href="mailto:contact@alwaz.travel" className="text-[#C9A961] hover:underline cursor-pointer">contact@alwaz.travel</a></p>
                <p><strong>Phone:</strong> <a href="tel:02034884455" className="text-[#C9A961] hover:underline cursor-pointer">0203 4884455</a></p>
                <p><strong>WhatsApp:</strong> <a href="https://wa.me/447362505895" target="_blank" rel="noopener noreferrer" className="text-[#C9A961] hover:underline cursor-pointer">+44 7362 505895</a></p>
              </div>
            </section>

            {/* Services Offered */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                3. Services Offered
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Alwaz Travel London provides a range of travel services including but not limited to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Umrah &amp; Hajj Packages:</strong> Spiritual pilgrimage travel arrangements including flights, accommodation, and ground services</li>
                  <li><strong>Holiday Packages:</strong> Customised and pre-packaged holiday travel to various destinations worldwide</li>
                  <li><strong>Flight Bookings:</strong> Domestic and international air ticket reservations</li>
                  <li><strong>Hotel Reservations:</strong> Accommodation bookings at hotels, resorts, and other lodging facilities</li>
                  <li><strong>Saudi Relocation Services:</strong> Assistance with relocation to Saudi Arabia including visa support and accommodation</li>
                  <li><strong>Visit London Services:</strong> Inbound travel services for visitors to London including accommodation and transfers</li>
                  <li><strong>Group Bookings:</strong> Tailored travel arrangements for groups and families</li>
                  <li><strong>Travel Consultation:</strong> Expert advice and personalised travel planning</li>
                </ul>
                <p>
                  We act as an intermediary between you and third-party service providers such as airlines, hotels, tour operators, and ground transport companies.
                </p>
              </div>
            </section>

            {/* Booking & Payment Terms */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                4. Booking &amp; Payment Terms
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <h3 className="text-xl font-semibold text-[#1A1F2E] mt-6">4.1 Booking Process</h3>
                <p>
                  All bookings are subject to availability and confirmation. A booking is only confirmed once you have received written confirmation from us via email or other written communication.
                </p>
                
                <h3 className="text-xl font-semibold text-[#1A1F2E] mt-6">4.2 Deposit &amp; Full Payment</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li>A non-refundable deposit is required at the time of booking. The deposit amount varies depending on the service and will be communicated to you at the time of booking.</li>
                  <li>Full payment must be received by the date specified in your booking confirmation, typically 8-12 weeks before departure for package holidays and Umrah/Hajj bookings.</li>
                  <li>Failure to pay the balance by the due date may result in automatic cancellation of your booking and forfeiture of your deposit.</li>
                </ul>

                <h3 className="text-xl font-semibold text-[#1A1F2E] mt-6">4.3 Payment Methods</h3>
                <p>
                  We accept payment via bank transfer, debit card, credit card, and other methods as communicated at the time of booking. Payment processing fees may apply for certain payment methods.
                </p>

                <h3 className="text-xl font-semibold text-[#1A1F2E] mt-6">4.4 Pricing</h3>
                <p>
                  All prices are quoted in British Pounds (GBP) unless otherwise stated. Prices are subject to change until a booking is confirmed and deposit is paid. We reserve the right to correct pricing errors on our website or in promotional materials.
                </p>

                <h3 className="text-xl font-semibold text-[#1A1F2E] mt-6">4.5 Price Variations</h3>
                <p>
                  Prices may be subject to surcharges for increases in transportation costs (including fuel), taxes, fees, or currency fluctuations. We will notify you of any price increases as soon as possible. You have the right to cancel your booking if the price increase exceeds 8% of the total holiday cost.
                </p>
              </div>
            </section>

            {/* Cancellation & Refund Policy */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                5. Cancellation &amp; Refund Policy
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <h3 className="text-xl font-semibold text-[#1A1F2E] mt-6">5.1 Cancellation by Customer</h3>
                <p>
                  If you wish to cancel your booking, you must notify us in writing via email to <a href="mailto:contact@alwaz.travel" className="text-[#C9A961] hover:underline cursor-pointer">contact@alwaz.travel</a>. Cancellation charges apply as follows:
                </p>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <ul className="space-y-2">
                    <li><strong>More than 90 days before departure:</strong> Loss of deposit</li>
                    <li><strong>90-60 days before departure:</strong> 50% of total booking cost</li>
                    <li><strong>59-30 days before departure:</strong> 75% of total booking cost</li>
                    <li><strong>Less than 30 days before departure:</strong> 100% of total booking cost (no refund)</li>
                  </ul>
                </div>
                <p className="text-sm italic">
                  Note: Cancellation charges may vary for special promotions, peak season bookings (Ramadan, Hajj, school holidays), and certain suppliers may impose stricter cancellation terms. Specific terms will be communicated at the time of booking.
                </p>

                <h3 className="text-xl font-semibold text-[#1A1F2E] mt-6">5.2 Cancellation by Alwaz Travel</h3>
                <p>
                  We reserve the right to cancel your booking in exceptional circumstances such as force majeure events, insufficient bookings, or failure to receive full payment. In such cases, we will offer you an alternative arrangement or a full refund of all monies paid.
                </p>

                <h3 className="text-xl font-semibold text-[#1A1F2E] mt-6">5.3 Refund Processing</h3>
                <p>
                  Refunds, where applicable, will be processed within 14-30 working days from the date of cancellation confirmation. Refunds will be made to the original payment method used for booking.
                </p>

                <h3 className="text-xl font-semibold text-[#1A1F2E] mt-6">5.4 Changes to Bookings</h3>
                <p>
                  If you wish to make changes to your confirmed booking (dates, names, destinations), we will do our best to accommodate your request. However, changes are subject to availability and may incur amendment fees from suppliers plus our administration fee of £50 per person per change.
                </p>
              </div>
            </section>

            {/* Travel Insurance */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                6. Travel Insurance
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  <strong>We strongly recommend that you purchase comprehensive travel insurance</strong> to cover cancellation, medical expenses, personal injury, loss of baggage, and other travel-related risks.
                </p>
                <p>
                  Travel insurance is <strong>mandatory</strong> for all Umrah and Hajj bookings. You must provide proof of adequate travel insurance before departure.
                </p>
                <p>
                  Alwaz Travel London is not responsible for any losses, expenses, or damages incurred due to your failure to obtain adequate travel insurance.
                </p>
              </div>
            </section>

            {/* Liability Limitations */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                7. Liability Limitations
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <h3 className="text-xl font-semibold text-[#1A1F2E] mt-6">7.1 Third-Party Service Providers</h3>
                <p>
                  We act as an agent for airlines, hotels, tour operators, and other service providers. We are not liable for any acts, omissions, defaults, or negligence of these third-party providers.
                </p>

                <h3 className="text-xl font-semibold text-[#1A1F2E] mt-6">7.2 Flight Delays &amp; Cancellations</h3>
                <p>
                  We are not responsible for flight delays, cancellations, or schedule changes made by airlines. Any compensation claims must be directed to the airline in accordance with their terms and applicable regulations (e.g., EU Regulation 261/2004).
                </p>

                <h3 className="text-xl font-semibold text-[#1A1F2E] mt-6">7.3 Personal Injury &amp; Property Damage</h3>
                <p>
                  We are not liable for any personal injury, illness, death, or loss of or damage to property unless caused by our proven negligence. Our liability is limited to the maximum extent permitted by law.
                </p>

                <h3 className="text-xl font-semibold text-[#1A1F2E] mt-6">7.4 Travel Documents</h3>
                <p>
                  You are responsible for ensuring that you have valid passports, visas, and all other required travel documents. We are not liable for any losses incurred due to invalid or missing travel documents.
                </p>

                <h3 className="text-xl font-semibold text-[#1A1F2E] mt-6">7.5 Maximum Liability</h3>
                <p>
                  Our total liability to you for any claims arising from your booking shall not exceed the total amount paid by you for the booking, except where liability cannot be excluded or limited by law.
                </p>
              </div>
            </section>

            {/* Force Majeure */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                8. Force Majeure
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  We shall not be liable for any failure or delay in performing our obligations due to circumstances beyond our reasonable control, including but not limited to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Acts of God (earthquakes, floods, storms, pandemics)</li>
                  <li>War, terrorism, civil unrest, or political instability</li>
                  <li>Government restrictions, travel bans, or border closures</li>
                  <li>Strikes, labour disputes, or industrial action</li>
                  <li>Technical failures or cyber-attacks</li>
                  <li>Supplier insolvency or bankruptcy</li>
                </ul>
                <p>
                  In the event of force majeure, we will make reasonable efforts to provide alternative arrangements or refunds where possible, but we are not obligated to do so.
                </p>
              </div>
            </section>

            {/* Intellectual Property */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                9. Intellectual Property
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  All content on our website, including text, graphics, logos, images, videos, and software, is the property of Nex Pro Travels Limited or its licensors and is protected by UK and international copyright, trademark, and intellectual property laws.
                </p>
                <p>
                  You may not reproduce, distribute, modify, or create derivative works from any content on our website without our prior written consent.
                </p>
              </div>
            </section>

            {/* Governing Law */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                10. Governing Law &amp; Jurisdiction
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  These Terms &amp; Conditions are governed by and construed in accordance with the laws of <strong>England and Wales</strong>.
                </p>
                <p>
                  Any disputes arising from these terms or your use of our services shall be subject to the exclusive jurisdiction of the courts of England and Wales.
                </p>
              </div>
            </section>

            {/* Dispute Resolution */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                11. Dispute Resolution
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  If you have a complaint or dispute regarding our services, please contact us first at <a href="mailto:contact@alwaz.travel" className="text-[#C9A961] hover:underline cursor-pointer">contact@alwaz.travel</a> or call us at <a href="tel:02034884455" className="text-[#C9A961] hover:underline cursor-pointer">0203 4884455</a>.
                </p>
                <p>
                  We will make every effort to resolve your complaint amicably within 28 days. If we are unable to resolve the matter to your satisfaction, you may refer the dispute to an independent Alternative Dispute Resolution (ADR) provider or pursue legal action through the courts.
                </p>
              </div>
            </section>

            {/* Customer Responsibilities */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                12. Customer Responsibilities
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  As a customer, you are responsible for:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Providing accurate and complete information when making a booking</li>
                  <li>Ensuring all names match exactly as they appear on passports</li>
                  <li>Obtaining valid passports, visas, and travel documents</li>
                  <li>Complying with health requirements (vaccinations, COVID-19 tests, etc.)</li>
                  <li>Arriving at airports and meeting points on time</li>
                  <li>Behaving appropriately and respecting local laws and customs</li>
                  <li>Ensuring you have adequate travel insurance</li>
                  <li>Reading and understanding all booking confirmations and travel documents</li>
                </ul>
              </div>
            </section>

            {/* Data Protection */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                13. Data Protection &amp; Privacy
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  We are committed to protecting your personal data in accordance with the UK Data Protection Act 2018 and the General Data Protection Regulation (GDPR).
                </p>
                <p>
                  For full details on how we collect, use, and protect your personal information, please refer to our <button onClick={() => window.REACT_APP_NAVIGATE('/privacy-policy')} className="text-[#C9A961] hover:underline cursor-pointer font-semibold">Privacy Policy</button>.
                </p>
              </div>
            </section>

            {/* Amendments */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                14. Amendments to Terms
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  We reserve the right to update or modify these Terms &amp; Conditions at any time without prior notice. Any changes will be posted on this page with an updated "Last Updated" date.
                </p>
                <p>
                  Your continued use of our website and services after any changes constitutes your acceptance of the revised terms.
                </p>
              </div>
            </section>

            {/* Contact Information */}
            <section>
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                15. Contact Information
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  If you have any questions about these Terms &amp; Conditions, please contact us:
                </p>
                <div className="bg-[#1A1F2E] text-white p-6 rounded-lg space-y-3">
                  <p><strong className="text-[#C9A961]">Alwaz Travel London</strong></p>
                  <p>Operated by Nex Pro Travels Limited</p>
                  <p>
                    <i className="ri-mail-line text-[#C9A961] mr-2"></i>
                    <a href="mailto:contact@alwaz.travel" className="hover:text-[#C9A961] transition-colors cursor-pointer">contact@alwaz.travel</a>
                  </p>
                  <p>
                    <i className="ri-phone-line text-[#C9A961] mr-2"></i>
                    <a href="tel:02034884455" className="hover:text-[#C9A961] transition-colors cursor-pointer">0203 4884455</a>
                  </p>
                  <p>
                    <i className="ri-whatsapp-line text-[#C9A961] mr-2"></i>
                    <a href="https://wa.me/447362505895" target="_blank" rel="noopener noreferrer" className="hover:text-[#C9A961] transition-colors cursor-pointer">+44 7362 505895</a>
                  </p>
                </div>
              </div>
            </section>

            {/* Acceptance */}
            <section className="bg-[#C9A961]/10 p-8 rounded-lg border-l-4 border-[#C9A961]">
              <h2 className="text-2xl font-bold text-[#1A1F2E] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                16. Acceptance of Terms
              </h2>
              <p className="text-gray-700 leading-relaxed">
                By making a booking with Alwaz Travel London, you acknowledge that you have read, understood, and agree to be bound by these Terms &amp; Conditions in their entirety.
              </p>
            </section>
          </div>

          {/* Back to Home */}
          <div className="mt-16 text-center">
            <button
              onClick={() => window.REACT_APP_NAVIGATE('/')}
              className="inline-flex items-center gap-2 bg-[#1A1F2E] text-white px-8 py-4 rounded-full hover:bg-[#C9A961] transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}